import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchFooters,
  createFooter,
  updateFooter,
  deleteFooter,
  setDefaultFooter,
  duplicateFooter,
  clearCurrentFooter
} from '../store/slices/footerSlice';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  StarIcon,
  DocumentDuplicateIcon,
  SwatchIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import FooterEditor from '../components/FooterEditor';
import FooterDesigner from '../components/FooterDesigner';
import ConfirmDialog from '../components/ConfirmDialog';
import '../styles/footer-preview.css';

const Footers = () => {
  const dispatch = useDispatch();
  const { items, loading, total } = useSelector((state) => state.footers);
  const { user } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [showDesigner, setShowDesigner] = useState(false);
  const [editingFooter, setEditingFooter] = useState(null);
  const [viewingFooter, setViewingFooter] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, footer: null, templates: [] });
  const [showGmailInstructions, setShowGmailInstructions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    htmlContent: '',
    cssStyles: '',
    isActive: true,
    designJson: null,
    allowedUserIds: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [individualSenders, setIndividualSenders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchFooters());
    
    // Fetch users with individual email sending enabled (Super Admin only)
    if (user?.role === 'Super Admin') {
      fetchIndividualSenders();
    }
  }, [dispatch, user]);

  const fetchIndividualSenders = async () => {
    try {
      const api = require('../utils/api').default;
      const response = await api.get('/users/individual-senders');
      setIndividualSenders(response.data);
    } catch (error) {
      console.error('Error fetching individual senders:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (editingFooter) {
        await dispatch(updateFooter({ id: editingFooter.id, data: formData })).unwrap();
        toast.success('Footer updated successfully');
      } else {
        await dispatch(createFooter(formData)).unwrap();
        toast.success('Footer created successfully');
      }

      setShowModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Failed to save footer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (footer) => {
    setEditingFooter(footer);
    setFormData({
      name: footer.name || '',
      description: footer.description || '',
      htmlContent: footer.htmlContent || '',
      cssStyles: footer.cssStyles || '',
      isActive: footer.isActive,
      designJson: footer.designJson || null,
      allowedUserIds: footer.allowedUserIds || null
    });
    setShowModal(true);
  };

  const handleDelete = async (footer) => {
    if (footer.isDefault) {
      toast.error('Cannot delete the default footer');
      return;
    }

    setDeleteDialog({ isOpen: true, footer, templates: [] });
  };

  const confirmDelete = async () => {
    if (deleteDialog.footer) {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await dispatch(deleteFooter({ id: deleteDialog.footer.id, force: false })).unwrap();
        toast.success('Footer deleted successfully');
        setDeleteDialog({ isOpen: false, footer: null, templates: [] });
      } catch (err) {
        console.error('Delete error:', err);

        // Handle specific error cases
        if (err.templates && err.templates.length > 0) {
          // Show templates that are using this footer
          setDeleteDialog(prev => ({ ...prev, templates: err.templates }));
          toast.error(err.message || 'Cannot delete footer - it is being used by templates');
        } else {
          toast.error(err.message || 'Failed to delete footer');
          setDeleteDialog({ isOpen: false, footer: null, templates: [] });
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const forceDelete = async () => {
    if (deleteDialog.footer) {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await dispatch(deleteFooter({ id: deleteDialog.footer.id, force: true })).unwrap();
        toast.success(`Footer deleted and ${deleteDialog.templates.length} templates updated successfully`);
        setDeleteDialog({ isOpen: false, footer: null, templates: [] });
      } catch (err) {
        console.error('Force delete error:', err);
        toast.error(err.message || 'Failed to delete footer and update templates');
        setDeleteDialog({ isOpen: false, footer: null, templates: [] });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ isOpen: false, footer: null, templates: [] });
  };

  const handleSetDefault = async (footer) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(setDefaultFooter(footer.id)).unwrap();
      toast.success('Default footer updated successfully');
    } catch (err) {
      toast.error('Failed to set default footer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (footer) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(duplicateFooter(footer.id)).unwrap();
      toast.success('Footer duplicated successfully');
    } catch (err) {
      toast.error('Failed to duplicate footer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDesigner = (footer = null) => {
    if (footer) {
      console.log('Opening footer in designer:', footer);
      console.log('Footer designJson:', footer.designJson);
      console.log('Footer designJson type:', typeof footer.designJson);
      
      setEditingFooter(footer);
      setFormData({
        name: footer.name || '',
        description: footer.description || '',
        htmlContent: footer.htmlContent || '',
        cssStyles: footer.cssStyles || '',
        isActive: footer.isActive !== false,
        designJson: footer.designJson || null,
        allowedUserIds: footer.allowedUserIds || null
      });
    } else {
      setEditingFooter(null);
      resetForm();
    }
    setShowDesigner(true);
  };

  const handleDesignerContentChange = (content, design) => {
    setFormData({ ...formData, htmlContent: content, designJson: design });
  };

  const handleDesignerClose = () => {
    setShowDesigner(false);
    setEditingFooter(null);
  };

  const resetForm = () => {
    setEditingFooter(null);
    setFormData({
      name: '',
      description: '',
      htmlContent: '',
      cssStyles: '',
      isActive: true,
      designJson: null,
      allowedUserIds: null
    });
    dispatch(clearCurrentFooter());
  };

  const canCreate = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canEdit = ['superadmin', 'global_admin_hq', 'country_director', 'institutional_relations_officer'].includes(user?.role);
  const canDelete = ['superadmin', 'global_admin_hq'].includes(user?.role);
  const canSetDefault = user?.role === 'superadmin';

  // Filter footers based on search term
  const filteredItems = items.filter(footer => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      footer.name?.toLowerCase().includes(search) ||
      footer.description?.toLowerCase().includes(search)
    );
  });

  // Get user names for display
  const getUserNames = (allowedUserIds) => {
    if (!allowedUserIds || allowedUserIds.length === 0) return null;
    return allowedUserIds
      .map(id => individualSenders.find(u => u.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  // Copy rendered signature for Gmail (using clipboard API with HTML)
  const handleCopyRenderedSignature = async (footer) => {
    try {
      // Create a temporary container
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = footer.htmlContent;
      
      // Extract only the body content (the actual signature)
      const bodyContent = tempDiv.querySelector('body');
      const signatureContent = bodyContent ? bodyContent.innerHTML : footer.htmlContent;
      
      // Create a clean div with the signature
      const cleanDiv = document.createElement('div');
      cleanDiv.innerHTML = signatureContent;
      cleanDiv.style.cssText = 'font-family: Arial, sans-serif; line-height: normal;';
      
      // Append to document temporarily (needed for selection)
      document.body.appendChild(cleanDiv);
      
      // Select the content
      const range = document.createRange();
      range.selectNodeContents(cleanDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Try to copy with rich formatting
      try {
        // Modern clipboard API with HTML support
        const htmlBlob = new Blob([signatureContent], { type: 'text/html' });
        const textBlob = new Blob([cleanDiv.innerText], { type: 'text/plain' });
        
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob
          })
        ]);
        
        toast.success('✅ Signature copied! Now paste it into Gmail (Cmd+V / Ctrl+V)', {
          autoClose: 5000
        });
      } catch (err) {
        // Fallback to execCommand
        document.execCommand('copy');
        toast.success('✅ Signature copied! Now paste it into Gmail (Cmd+V / Ctrl+V)', {
          autoClose: 5000
        });
      }
      
      // Clean up
      selection.removeAllRanges();
      document.body.removeChild(cleanDiv);
      
      // Show instructions
      setShowGmailInstructions(true);
      
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy signature. Please try the manual selection method.');
    }
  };

  // Export footer as DOCX
  const handleExportDocx = async (footer) => {
    try {
      toast.info('Preparing DOCX export...');
      
      // For now, we'll create a simple HTML file that can be opened in Word
      // A full DOCX export would require a library like docx or mammoth
      const blob = new Blob([footer.htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${footer.name.replace(/[^a-z0-9]/gi, '_')}_footer.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Footer exported! Open in Microsoft Word to save as DOCX.');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export footer');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Email Footers</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 font-medium">
            Manage email footer templates ({total} footers)
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleDesigner()}
            className="flex items-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
          >
            <SwatchIcon className="h-5 w-5 mr-2" />
            Create New Footer
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <input
          type="text"
          placeholder="Search footers by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((footer) => (
            <div key={footer.id} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-200 hover:scale-105">
              {/* Footer Preview */}
              <div className="h-32 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 relative border-b-2 border-gray-200">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="transform scale-[0.3] origin-center w-[300%] h-[300%] flex items-center justify-center"
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: 'normal'
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: footer.htmlContent }} />
                  </div>
                </div>
                {footer.isDefault && (
                  <div className="absolute top-2 right-2 z-10">
                    <StarSolidIcon className="h-6 w-6 text-yellow-500 drop-shadow-lg" />
                  </div>
                )}
                <div className="absolute bottom-2 left-2 z-10">
                  <span className="text-xs bg-black bg-opacity-60 text-white px-2 py-1 rounded-lg font-medium">
                    Preview
                  </span>
                </div>
              </div>

              {/* Footer Info */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {footer.name}
                  </h3>
                  <span className={`px-3 py-1 text-xs rounded-full font-bold ${footer.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}>
                    {footer.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {footer.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {footer.description}
                  </p>
                )}

                {/* Assigned Users Info - Super Admin Only */}
                {user?.role === 'Super Admin' && footer.allowedUserIds && footer.allowedUserIds.length > 0 && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700 font-semibold mb-1">Assigned to:</p>
                    <p className="text-xs text-blue-600">
                      {getUserNames(footer.allowedUserIds) || `${footer.allowedUserIds.length} user(s)`}
                    </p>
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-4 font-medium">
                  Created: {new Date(footer.createdAt).toLocaleDateString()}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingFooter(footer)}
                      className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-lg transition-all duration-200"
                      title="View Footer"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>

                    {canEdit && (
                      <button
                        onClick={() => handleDesigner(footer)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-all duration-200"
                        title="Edit Footer"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}

                    <button
                      onClick={() => handleDuplicate(footer)}
                      className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-2 rounded-lg transition-all duration-200"
                      title="Duplicate Footer"
                    >
                      <DocumentDuplicateIcon className="h-5 w-5" />
                    </button>

                    {canDelete && !footer.isDefault && (
                      <button
                        onClick={() => handleDelete(footer)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-all duration-200"
                        title="Delete Footer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyRenderedSignature(footer)}
                      className="flex-1 text-xs text-purple-600 hover:text-purple-700 font-semibold hover:bg-purple-50 px-2 py-1 rounded-lg transition-all duration-200 flex items-center justify-center gap-1"
                      title="Copy Rendered Signature for Gmail"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy for Gmail
                    </button>

                    <button
                      onClick={() => handleExportDocx(footer)}
                      className="flex-1 text-xs text-orange-600 hover:text-orange-700 font-semibold hover:bg-orange-50 px-2 py-1 rounded-lg transition-all duration-200 flex items-center justify-center gap-1"
                      title="Export as HTML/DOCX"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Export
                    </button>
                  </div>

                  {canSetDefault && !footer.isDefault && (
                    <button
                      onClick={() => handleSetDefault(footer)}
                      className="flex items-center justify-center text-xs text-yellow-600 hover:text-yellow-700 font-semibold hover:bg-yellow-50 px-2 py-1 rounded-lg transition-all duration-200"
                      title="Set as Default"
                    >
                      <StarIcon className="h-4 w-4 mr-1" />
                      Set Default
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No footer templates found.</p>
          {canCreate && (
            <button
              onClick={() => setShowModal(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first footer template
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Footer Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {editingFooter ? 'Edit Footer Template' : 'Create New Footer Template'}
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Basic Info */}
                    <div className="lg:col-span-1 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Footer Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Company Footer, Newsletter Footer"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Brief description of this footer template"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom CSS (Optional)
                        </label>
                        <textarea
                          value={formData.cssStyles}
                          onChange={(e) => setFormData({ ...formData, cssStyles: e.target.value })}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="/* Custom CSS styles */
.footer-custom {
  background: #f8f9fa;
  padding: 20px;
}"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                          Active Footer
                        </label>
                      </div>

                      {/* User Assignment - Super Admin Only */}
                      {user?.role === 'Super Admin' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign to Individual Senders (Optional)
                          </label>
                          <p className="text-xs text-gray-500 mb-3">
                            Leave empty to make this footer available to all users. Select specific users to restrict access.
                          </p>
                          
                          <div className="space-y-3">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="allUsersFooter"
                                name="userAccessFooter"
                                checked={formData.allowedUserIds === null}
                                onChange={() => setFormData({ ...formData, allowedUserIds: null })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label htmlFor="allUsersFooter" className="ml-2 block text-sm text-gray-900">
                                Available to all users
                              </label>
                            </div>

                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="specificUsersFooter"
                                name="userAccessFooter"
                                checked={formData.allowedUserIds !== null}
                                onChange={() => setFormData({ ...formData, allowedUserIds: [] })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <label htmlFor="specificUsersFooter" className="ml-2 block text-sm text-gray-900">
                                Assign to specific individual senders
                              </label>
                            </div>

                            {formData.allowedUserIds !== null && (
                              <div className="ml-6 space-y-2">
                                {individualSenders.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic">
                                    No users with individual email sending enabled
                                  </p>
                                ) : (
                                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-3 space-y-2">
                                    {individualSenders.map(sender => (
                                      <div key={sender.id} className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={`footer-user-${sender.id}`}
                                          checked={formData.allowedUserIds?.includes(sender.id) || false}
                                          onChange={(e) => {
                                            const currentIds = formData.allowedUserIds || [];
                                            if (e.target.checked) {
                                              setFormData({ 
                                                ...formData, 
                                                allowedUserIds: [...currentIds, sender.id] 
                                              });
                                            } else {
                                              setFormData({ 
                                                ...formData, 
                                                allowedUserIds: currentIds.filter(id => id !== sender.id) 
                                              });
                                            }
                                          }}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor={`footer-user-${sender.id}`} className="ml-2 block text-sm text-gray-700">
                                          {sender.name} ({sender.sendingEmail || sender.email})
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Editor */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Footer Content *
                      </label>
                      <FooterEditor
                        value={formData.htmlContent}
                        onChange={(html) => setFormData({ ...formData, htmlContent: html })}
                        placeholder="Design your email footer here..."
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                    {isSubmitting ? (editingFooter ? 'Updating...' : 'Creating...') : (editingFooter ? 'Update Footer' : 'Create Footer')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Footer Modal */}
      {viewingFooter && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setViewingFooter(null)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{viewingFooter.name}</h3>
                    {viewingFooter.description && (
                      <p className="text-gray-600 mt-1">{viewingFooter.description}</p>
                    )}
                  </div>
                  {viewingFooter.isDefault && (
                    <span className="flex items-center text-yellow-600">
                      <StarSolidIcon className="h-5 w-5 mr-1" />
                      Default Footer
                    </span>
                  )}
                </div>

                <div className="border rounded-lg bg-gray-50 mb-4 overflow-hidden">
                  <h4 className="text-sm font-medium text-gray-700 p-3 bg-gray-100 border-b">Footer Preview:</h4>

                  {/* Email Context Preview */}
                  <div className="bg-white">
                    {/* Simulated Email Header */}
                    <div className="p-4 border-b bg-gray-50">
                      <div className="text-sm text-gray-600 mb-1" style={{ lineHeight: 'normal' }}>Email Context</div>
                      <div className="text-base font-semibold text-gray-900 mb-1" style={{ lineHeight: 'normal' }}>Sample Email Subject</div>
                      <div className="text-sm text-gray-500" style={{ lineHeight: 'normal' }}>From: info@twilightfinland.eu</div>
                    </div>

                    {/* Email Body */}
                    <div className="p-4 border-b">
                      <div style={{ color: '#374151', lineHeight: 'normal', fontSize: '14px' }}>
                        <p style={{ margin: '0 0 12px 0' }}>Dear Recipient,</p>
                        <p style={{ margin: '0 0 12px 0' }}>This is how your footer will appear in actual emails sent to your contacts.</p>
                        <p style={{ margin: '0' }}>Best regards,<br />TwilightX Mail Team</p>
                      </div>
                    </div>

                    {/* Footer Preview */}
                    <div className="flex justify-center bg-white">
                      <div
                        className="footer-full-preview w-full"
                        dangerouslySetInnerHTML={{ __html: viewingFooter.htmlContent }}
                        style={{
                          fontFamily: 'Arial, sans-serif',
                          lineHeight: 'normal',
                          color: '#333',
                          maxWidth: '650px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {viewingFooter.cssStyles && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Custom CSS:</h4>
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                      {viewingFooter.cssStyles}
                    </pre>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  onClick={() => {
                    handleCopyRenderedSignature(viewingFooter);
                  }}
                  className="w-full sm:w-auto px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  Copy for Gmail
                </button>
                <button
                  onClick={() => setViewingFooter(null)}
                  className="w-full sm:w-auto mt-3 sm:mt-0 px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visual Designer Modal */}
      {showDesigner && (
        <div className="fixed inset-0 z-50">
          <FooterDesigner
            initialContent={formData.htmlContent}
            initialDesign={formData.designJson}
            onContentChange={handleDesignerContentChange}
            onClose={handleDesignerClose}
            footerData={formData}
            onFooterDataChange={setFormData}
            onSave={async (html, design) => {
              if (!formData.name) {
                toast.error('Please provide footer name');
                return;
              }
              try {
                // Use the passed html and design if available, otherwise fallback to state
                // This ensures we have the freshest data from the editor export
                const dataToSave = {
                  ...formData,
                  htmlContent: html || formData.htmlContent,
                  designJson: design || formData.designJson
                };

                if (editingFooter) {
                  await dispatch(updateFooter({ id: editingFooter.id, data: dataToSave })).unwrap();
                  toast.success('Footer updated successfully');
                } else {
                  await dispatch(createFooter(dataToSave)).unwrap();
                  toast.success('Footer created successfully');
                }
                setShowDesigner(false);
                resetForm();
              } catch (err) {
                toast.error(editingFooter ? 'Failed to update footer' : 'Failed to create footer');
              }
            }}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={cancelDelete}
        onConfirm={deleteDialog.templates.length > 0 ? cancelDelete : confirmDelete}
        onSecondaryAction={deleteDialog.templates.length > 0 ? forceDelete : undefined}
        title="Delete Footer"
        message={
          deleteDialog.templates.length > 0
            ? `Cannot delete "${deleteDialog.footer?.name}" because it is being used by ${deleteDialog.templates.length} template(s): ${deleteDialog.templates.map(t => t.name).join(', ')}. You can either update those templates to use a different footer, or remove the footer from those templates.`
            : `Are you sure you want to delete "${deleteDialog.footer?.name}"? This action cannot be undone.`
        }
        confirmText={deleteDialog.templates.length > 0 ? "OK" : "Delete Footer"}
        secondaryText={deleteDialog.templates.length > 0 ? "Remove from Templates & Delete" : undefined}
        cancelText={deleteDialog.templates.length > 0 ? "Cancel" : "Cancel"}
        type="danger"
      />

      {/* Gmail Instructions Modal */}
      {showGmailInstructions && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowGmailInstructions(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start mb-4">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ClipboardDocumentIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-xl font-bold text-gray-900">
                      ✅ Signature Copied to Clipboard!
                    </h3>
                    <p className="text-sm text-green-600 font-semibold mt-1">
                      Now paste it into Gmail signature settings
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                    <h4 className="font-bold text-green-900 mb-3 text-lg">✅ Next Steps (Super Easy!)</h4>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-green-900">
                      <li className="font-semibold">
                        Go to Gmail Settings
                        <p className="ml-6 mt-1 font-normal text-green-800">
                          Click ⚙️ gear icon → "See all settings" → Scroll to "Signature"
                        </p>
                      </li>
                      <li className="font-semibold">
                        Click your signature name (e.g., "Twilight Finland") or "Create new"
                      </li>
                      <li className="font-semibold">
                        Click inside the signature box and paste
                        <p className="ml-6 mt-1 font-normal text-green-800">
                          Mac: <code className="bg-green-200 px-2 py-1 rounded font-mono">Cmd + V</code> | 
                          Windows: <code className="bg-green-200 px-2 py-1 rounded font-mono">Ctrl + V</code>
                        </p>
                      </li>
                      <li className="font-semibold">
                        Scroll down and click "Save Changes"
                      </li>
                      <li className="font-semibold">
                        Test by composing a new email to yourself!
                      </li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">🔥 What Just Happened:</h4>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>✅ Your signature was copied with full formatting</li>
                      <li>✅ All images, links, and styling are preserved</li>
                      <li>✅ Images are hosted online (will work in all emails)</li>
                      <li>✅ Links remain clickable</li>
                      <li>✅ Ready to paste directly into Gmail</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">💡 Pro Tips:</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                      <li>Use Chrome or Firefox for best results</li>
                      <li>Gmail may slightly adjust styling - this is normal</li>
                      <li>If paste doesn't work, click "Copy for Gmail" button again</li>
                      <li>You can also use the "Export" button to save as HTML file</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border border-purple-300 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-2">📄 Need DOCX with Embedded Images?</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-purple-800">
                      <li>Click the "Export" button on the footer card</li>
                      <li>Open the downloaded HTML file in Microsoft Word</li>
                      <li>Word will render it with all images embedded</li>
                      <li>Save As → DOCX format</li>
                      <li>All images and links will be preserved!</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowGmailInstructions(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                >
                  Got it! Opening Gmail now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Footers;